import React, { useState, useEffect } from 'react';
import { Link, useLocation, useNavigate } from 'react-router-dom';
import {
  Box,
  Button,
  Card,
  CardBody,
  Checkbox,
  FormControl,
  FormLabel,
  Heading,
  Input,
  InputGroup,
  InputRightElement,
  Stack,
  Text,
  VStack,
  Alert,
  AlertIcon,
  Container,
  Flex,
  Icon,
  useColorModeValue,
} from '@chakra-ui/react';
import { ViewIcon, ViewOffIcon } from '@chakra-ui/icons';
import NewCompassLogo from '../common/NewCompassLogo';
import { useForm } from 'react-hook-form';
import { useAuth } from '../../contexts/AuthContext';
import { useAppInfoContext } from '../../contexts/AppInfoContext';

interface LoginFormData {
  username: string;
  password: string;
  rememberMe: boolean;
}

export const Login: React.FC = () => {
  const [showPassword, setShowPassword] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const { login, isAuthenticated, isLoading } = useAuth();
  const { appInfo } = useAppInfoContext();
  const location = useLocation();
  const navigate = useNavigate();

  const {
    register,
    handleSubmit,
    formState: { errors, isSubmitting },
  } = useForm<LoginFormData>();

  const bgColor = useColorModeValue('white', 'gray.800');
  const borderColor = useColorModeValue('gray.200', 'gray.700');
  const textColor = useColorModeValue('gray.900', 'white');
  const mutedColor = useColorModeValue('gray.600', 'gray.400');

  // Get the return URL from location state (passed by ProtectedRoute)
  const locationState = location.state as any;
  const from = locationState?.from?.pathname || locationState?.from || '/';

  useEffect(() => {
    if (isAuthenticated) {
      // Redirect to the return URL if authenticated
      navigate(from, { replace: true });
    }
  }, [isAuthenticated, navigate, from]);

  const onSubmit = async (data: LoginFormData) => {
    try {
      setError(null);
      console.log('Login attempt with rememberMe:', data.rememberMe);
      await login(data.username, data.password, { rememberMe: data.rememberMe, returnUrl: from });
      console.log('Login successful, rememberMe was:', data.rememberMe);
    } catch (err: any) {
      console.error('Login failed:', err);
      setError(err.message || 'Login failed. Please check your credentials.');
    }
  };

  return (
    <Container maxW="lg" py={{ base: '12', md: '24' }} px={{ base: '0', sm: '8' }}>
      <Stack spacing="8">
        <Box
          py={{ base: '0', sm: '8' }}
          px={{ base: '4', sm: '10' }}
          bg={bgColor}
          boxShadow={{ base: 'none', sm: 'md' }}
          borderRadius={{ base: 'none', sm: 'xl' }}
          border="1px"
          borderColor={borderColor}
        >
          <Stack spacing="6">
            <VStack spacing="6">
              <Flex align="center" justify="center">
                <NewCompassLogo boxSize={16} />
              </Flex>
              <Stack spacing="2" textAlign="center">
                <Heading size="lg" color={textColor}>
                  Welcome to {appInfo.name}
                </Heading>
                <Text color={mutedColor}>Sign in to continue</Text>
              </Stack>
            </VStack>

            <Box as="form" onSubmit={handleSubmit(onSubmit)}>
              <Stack spacing="6">
                <Stack spacing="5">
                  <FormControl isRequired>
                    <FormLabel color={textColor}>Username or Email</FormLabel>
                    <Input
                      id="username"
                      type="text"
                      placeholder="Enter your username or email"
                      {...register('username', { required: 'Username or email is required' })}
                      autoFocus
                    />
                    {errors.username && <Text color="red.500" fontSize="sm">{errors.username.message}</Text>}
                  </FormControl>

                  <FormControl isInvalid={!!errors.password}>
                    <FormLabel htmlFor="password" color={textColor}>
                      Password
                    </FormLabel>
                    <InputGroup>
                      <Input
                        id="password"
                        type={showPassword ? 'text' : 'password'}
                        placeholder="Enter your password"
                        {...register('password', {
                          required: 'Password is required',
                        })}
                      />
                      <InputRightElement>
                        <Button
                          variant="ghost"
                          h="1.75rem"
                          size="sm"
                          onClick={() => setShowPassword(!showPassword)}
                        >
                          {showPassword ? <ViewOffIcon /> : <ViewIcon />}
                        </Button>
                      </InputRightElement>
                    </InputGroup>
                    {errors.password && (
                      <Text color="red.500" fontSize="sm">
                        {errors.password.message}
                      </Text>
                    )}
                  </FormControl>
                </Stack>

                <Stack spacing="6">
                  <Stack direction="row" align="center" justify="space-between">
                    <Checkbox {...register('rememberMe')} colorScheme="primary">
                      Remember me
                    </Checkbox>
                    <Button
                      as={Link}
                      to="/auth/password-reset"
                      variant="link"
                      colorScheme="primary"
                      size="sm"
                    >
                      Forgot password?
                    </Button>
                  </Stack>

                  {error && (
                    <Alert status="error" borderRadius="md">
                      <AlertIcon />
                      {error}
                    </Alert>
                  )}

                  <Button
                    type="submit"
                    colorScheme="primary"
                    size="lg"
                    fontSize="md"
                    isLoading={isSubmitting || isLoading}
                    loadingText="Signing in..."
                  >
                    Sign in
                  </Button>
                </Stack>
              </Stack>
            </Box>
            
            {/* Debug information - remove in production */}
            {process.env.NODE_ENV === 'development' && (
              <Box
                p={4}
                bg="gray.100"
                borderRadius="md"
                fontSize="sm"
                color="gray.600"
                _dark={{ bg: 'gray.700', color: 'gray.300' }}
              >
                <Text fontWeight="bold" mb={2}>Debug Info:</Text>
                <Text>Local Storage Token: {localStorage.getItem('authToken') ? 'Present' : 'None'}</Text>
                <Text>Cookie Token: {document.cookie.includes('authToken') ? 'Present' : 'None'}</Text>
                <Text>Remember Me Cookie: {document.cookie.includes('rememberMe=true') ? 'Present' : 'None'}</Text>
                <Text>Current Cookies: {document.cookie || 'None'}</Text>
              </Box>
            )}
          </Stack>
        </Box>
      </Stack>
    </Container>
  );
};